import styles from '@/components/static/StudyMaterialsGuidePage/StudyMaterialsGuidePage.module.scss'
import React, { ReactElement, useMemo } from 'react'
import { BaseGuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponent/BaseGuideComponent'
import { GuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/GuideComponent.types'
import { Screenshot } from '@/components/static/images/Screenshot/Screenshot'
import { ColorMap, getJepColor } from '@/components/static/ui/consts'
import { LinkIfLoggedInOtherwiseText } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/LinkIfLoggedInOtherwiseText/LinkIfLoggedInOtherwiseText'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import Tooltip from 'antd/lib/tooltip'
import { BackToTop } from '@/components/static/BackToTop/BackToTop'
import { ClickableArrow } from '@/components/ClickableArrow/ClickableArrow'
import { OwlPageHeader } from '@/components/OwlPageHeader/OwlPageHeader'
import { OwlOutline } from '@/components/static/images/owlBranding/outlineOwl'

export const OutlinesGuidePage: React.FC<GuideComponent> = (
    props
): ReactElement => {
    const color = useMemo((): ColorMap => {
        return getJepColor(props.courseConfigType)
    }, [props.courseConfigType])
    return (
        <div className={styles.studyMaterialsGuidePageBackground}>
            <BackToTop isLoggedIn={props.isLoggedIn} />

            <div className={styles.studyMaterialsGuidePage}>
                <div className={styles.headerContainer}>
                    <div className={reusableCssClass.centerChildrenVertically}>
                        <Tooltip
                            title={'Return to Overview'}
                            placement={'right'}
                        >
                            <ClickableArrow
                                orientation={'left'}
                                linkDestination={
                                    UiLayoutConstant.STUDY_MATERIALS_GUIDE_PAGE
                                }
                            />
                        </Tooltip>
                    </div>
                    <div className={styles.owlHeader}>
                        <OwlPageHeader
                            text={'Outlines'}
                            owlComponent={<OwlOutline />}
                        />
                    </div>
                </div>

                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Summary</div>
                            <p>
                                Outlines are the most detailed component of our
                                content offerings and are the closest you need
                                to get to the source material. Our course
                                instructors have gone through both the source
                                material and previous exams at a high level of
                                detail, and filtered down a thousand pages of
                                content to only retain testable information and
                                communicating it to you in a simpler, more
                                digestible manner.
                            </p>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/outlineScreenshotWithTimer.webp'
                                }
                                alt={'screenshot of outline'}
                            />
                            <br />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>How To Use</div>
                            <ol>
                                <li>
                                    Read through all the outlines once before
                                    you use any other piece of content to gain
                                    familiarity.
                                </li>
                                <li>
                                    Keep track of your completion progress, star
                                    specific sections to refer back to later,
                                    and begin / participate in comment threads
                                    with other students and your instructor.
                                </li>
                                <li>
                                    Try out the practice problems embedded in
                                    the outline to understand how this specific
                                    topic has been asked in previous exams.
                                </li>
                                <li>
                                    Using the built-in search mechanism, refer
                                    back to specific outlines as needed when
                                    studying notecards and practice problems in
                                    order to build more context and improve
                                    retention.
                                </li>
                                <li>
                                    Read through the outlines one more time as
                                    you get closer to the exam, to fill in any
                                    important gaps you may have forgotten since
                                    the first read-through.
                                </li>
                            </ol>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/outlineCommentThread.webp'
                                }
                                alt={'screenshot of outline comment thread'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Extra Goodies</div>
                            <ul>
                                <li>
                                    Use the{' '}
                                    <LinkIfLoggedInOtherwiseText
                                        text={'outlines dashboard'}
                                        relativePath={
                                            UiLayoutConstant.OUTLINES_DASHBOARD
                                        }
                                        isLoggedIn={props.isLoggedIn}
                                    />{' '}
                                    to view your progress and find the next
                                    outline to study.
                                </li>
                                <li>
                                    Marking an outline as read updates the
                                    dashboard with your progress.
                                </li>
                                <li>
                                    Starring specific sections of text allow you
                                    to refer back to them later.
                                </li>
                                <li>
                                    Have a question about a specific part of the
                                    text? Simply highlight the text and start a
                                    comment thread. Your instructor will get
                                    back to you within 24 hours.
                                </li>
                                <li>
                                    Want to study outlines offline? You can
                                    download / print our outlines out and study
                                    them at your convenience.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/outlinesDashboardScreenshotWithTimer.webp'
                                }
                                alt={'screenshot of outlines dashboard'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
            </div>
        </div>
    )
}
